import { Trans, useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import TrackingIssues, { TrackingIssueMessage } from '../issues';
import TrackingCase from '../case-details';
import TrackingWindow from '../window';
import { hasDeliveryIssue } from '../utils/mapParcelStage';
import { CreateCaseNoteResponse, Tracking, TrackingDetails, TrackingEvent } from '~/lib/types/tracking-types';
import { P2GGrid, P2GLink, P2GText } from '@parcel2go/core-ui';
import {
  GetLastTrackingEventTimeStamp,
  GetLastTrackingEventTimeStampSingleParcel,
  GetRelativeTimestampByDays,
  HasEstimatedDeliveryDatePassed
} from '~/lib/utils/tracking';
import TrackingCollection from '../collection';
import { TranslationNamespaces } from '~/lib/constants';
import { useTenant } from '@parcel2go/npm-p2g-app';
import { PublicSettings } from '~/lib/ui-serverless-settings';

interface ITrackingNotifications {
  tracking: Tracking;
  onOpenValidation: () => void;
  onSendReply: (caseId: string, note: string) => Promise<CreateCaseNoteResponse>;
  withWindow?: TrackingEvent | null;
  parcelNumbers: string[];
  isPerParcelNotification?: boolean;
  currentParcel?: TrackingDetails;
}

const useStyles = makeStyles()(() => ({
  textSection: {
    flexDirection: 'column',
    justifyDirection: 'flex-start',
    alignItems: 'stretch',
    marginBottom: '8px'
  }
}));

const TrackingNotifications = ({
  tracking,
  withWindow,
  currentParcel,
  parcelNumbers,
  isPerParcelNotification,
  onOpenValidation,
  onSendReply
}: ITrackingNotifications) => {
  const { classes } = useStyles();
  const { t } = useTranslation(TranslationNamespaces.TRACKING);

  const estimatedDeliveryHasPassed = HasEstimatedDeliveryDatePassed(tracking.order.estimatedDeliveryDate);

  const { settings } = useTenant();
  const websiteUrl = settings[PublicSettings.WEBSITE];

  if (parcelNumbers.length <= 1 && !isPerParcelNotification) {
    const lastTrackingEventScan = GetLastTrackingEventTimeStamp(tracking);

    const isNotTrackingInTransit = lastTrackingEventScan
      ? !tracking.complete &&
        !estimatedDeliveryHasPassed &&
        !tracking.hasRTSParcel &&
        !tracking.hasHeldInCustomsParcel &&
        lastTrackingEventScan.getTime() < GetRelativeTimestampByDays(4)
      : false;

    if (tracking.isOwner && tracking.case !== null) {
      return (
        <>
          {tracking.order.foundOrder && withWindow?.type !== 'Collection Scheduled' && (
            <P2GGrid item container classNames={classes.textSection}>
              <TrackingCase caseDetails={tracking.case} onSendReply={onSendReply} />
            </P2GGrid>
          )}
        </>
      );
    }

    if (!tracking.isOwner && tracking.case) {
      return (
        <P2GGrid item container classNames={classes.textSection}>
          <TrackingIssues
            tracking={tracking}
            onOpenValidation={onOpenValidation}
            headingVariant="h2"
            courierName={tracking.order.service.courierName}
          />
        </P2GGrid>
      );
    }

    return (
      <>
        {!tracking.complete &&
          !withWindow &&
          !(tracking.hasIssues || tracking.isLate || tracking.possiablyLost || isNotTrackingInTransit) &&
          tracking.started && (
            <P2GGrid item container classNames={classes.textSection}>
              <P2GText variant="h2" component="h2" data-testid="tracking-notification-heading">
                <Trans
                  t={t}
                  i18nKey="HISTORY_COURIER"
                  values={{ courier: tracking.order.service.courierName }}
                  defaults="Your {{ courier }} parcel is on it's way!"
                />
              </P2GText>
              <P2GText variant="body1" component="p" sx={{ py: 2 }}>
                <Trans t={t} i18nKey="HISTORY_SERVICE_LEVEL_AGREEMENT">
                  Please allow for 24hrs for the tracking to update.
                </Trans>
              </P2GText>
            </P2GGrid>
          )}

        {!tracking.complete && withWindow && (
          <P2GGrid item>
            <TrackingWindow event={withWindow} started={tracking.started} headingVariant="h2" />
          </P2GGrid>
        )}

        {tracking.complete &&
          tracking.case === null &&
          !tracking.hasRTSParcel &&
          withWindow?.type !== 'Collection Scheduled' && (
            <P2GGrid item container classNames={classes.textSection}>
              <P2GText variant="h2" component="h2" dataTestId="tracking-notification-heading">
                <Trans t={t} i18nKey="RESULT_DELIVERED_TITLE" defaults="Your parcel has been delivered" />
              </P2GText>
              <P2GText variant="body1" component="p" sx={{ py: 2 }}>
                <Trans
                  t={t}
                  i18nKey="RESULT_DELIVERED_DESCRIPTION"
                  defaults="Your parcel has been successfully delivered. <0>If there has been an issue with your parcel please click here</0>"
                  components={[<P2GLink href={`${websiteUrl}/service-centre/select/choose`} />]}
                />
              </P2GText>
            </P2GGrid>
          )}

        {tracking.complete &&
          tracking.case === null &&
          tracking.hasRTSParcel &&
          withWindow?.type !== 'Collection Scheduled' && (
            <P2GGrid item container classNames={classes.textSection}>
              <TrackingIssueMessage
                orderReference={tracking.order.orderLineId}
                color="error"
                title={t('RESULT_RTS_DELIVERED_TITLE', {
                  defaultValue: 'Your {{courier}} parcel has been delivered back to the sender',
                  courier: tracking.order.service.courierName
                })}
                message={
                  <Trans
                    t={t}
                    i18nKey="RESULT_RTS_DELIVERED_DESCRIPTION"
                    defaults="Unfortunately the parcel has been returned to the sender due to an issue with the delivery, we apologise for any inconvenience.  If the sender has not received the parcel back please <0>click here</0>"
                    components={[<P2GLink href={`${websiteUrl}/service-centre/select/choose`} />]}
                  />
                }
                showChat={false}
                showContact={false}
                headingVariant="h2"
                showOptions={true}
                onValidateOrder={onOpenValidation}
              />
            </P2GGrid>
          )}

        {tracking.order.foundOrder &&
          !tracking.started &&
          !tracking.order.service.isDropOff &&
          withWindow?.type !== 'Collection Scheduled' && (
            <P2GGrid item container classNames={classes.textSection}>
              <TrackingCollection
                hasAddressIssue={tracking.hasAddressIssueParcel}
                hasAttemptedCollection={tracking.hasAttemptedCollectionParcel}
                hasCancelled={tracking.hasCancelledParcel}
                isOwner={tracking.isOwner}
                orderReference={tracking.order.orderLineId}
                headingVariant="h2"
              />
            </P2GGrid>
          )}

        {tracking.order.foundOrder &&
          !tracking.started &&
          tracking.order.service.isDropOff &&
          withWindow?.type !== 'Collection Scheduled' && (
            <P2GGrid item container classNames={classes.textSection}>
              <P2GText variant="h2" component="h2">
                <Trans t={t} i18nKey="RESULT_DROPOFF_TITLE" defaults="Waiting for confirmation from the courier" />
              </P2GText>
              <P2GText variant="body1" component="p" sx={{ py: 2 }}>
                <Trans
                  t={t}
                  i18nKey="RESULT_DROPOFF_DESCRIPTION"
                  defaults="We are waiting for confirmation that the courier has your parcel.  If you already dropped off your parcel, please allow up to 24 hrs for the tracking to update."
                />
              </P2GText>
            </P2GGrid>
          )}

        {tracking.started &&
          tracking.order.foundOrder &&
          (tracking.hasIssues || tracking.isLate || tracking.possiablyLost || isNotTrackingInTransit) &&
          withWindow?.type !== 'Collection Scheduled' && (
            <P2GGrid item container classNames={classes.textSection}>
              <TrackingIssues
                tracking={tracking}
                onOpenValidation={onOpenValidation}
                headingVariant="h2"
                courierName={tracking.order.service.courierName}
              />
            </P2GGrid>
          )}
      </>
    );
  }

  if (parcelNumbers.length > 1 && !isPerParcelNotification) {
    if (tracking.isOwner && tracking.case !== null) {
      return (
        <>
          {tracking.order.foundOrder && withWindow?.type !== 'Collection Scheduled' && (
            <P2GGrid item container classNames={classes.textSection}>
              <TrackingCase caseDetails={tracking.case} onSendReply={onSendReply} />
            </P2GGrid>
          )}
        </>
      );
    }

    if (!tracking.isOwner && tracking.case) {
      return (
        <P2GGrid item container classNames={classes.textSection}>
          <TrackingIssues
            tracking={tracking}
            onOpenValidation={onOpenValidation}
            headingVariant="h2"
            courierName={tracking.order.service.courierName}
          />
        </P2GGrid>
      );
    }

    if (
      tracking.order.foundOrder &&
      !tracking.started &&
      tracking.order.service.isDropOff &&
      withWindow?.type !== 'Collection Scheduled'
    ) {
      return (
        <P2GGrid item container classNames={classes.textSection}>
          <P2GText variant="h2" component="h2">
            <Trans t={t} i18nKey="RESULT_DROPOFF_TITLE" defaults="Waiting for confirmation from the courier" />
          </P2GText>
          <P2GText variant="body1" component="p" sx={{ py: 2 }}>
            <Trans
              t={t}
              i18nKey="RESULT_DROPOFF_DESCRIPTION"
              defaults="We are waiting for confirmation that the courier has your parcel.  If you already dropped off your parcel, please allow up to 24 hrs for the tracking to update."
            />
          </P2GText>
        </P2GGrid>
      );
    }

    return (
      <>
        {!tracking.case && (
          <P2GGrid item container classNames={classes.textSection}>
            <P2GText variant="h2" component="h2">
              <Trans
                t={t}
                i18nKey="HISTORY_OVERVIEW_MULTIPARCEL_TITLE"
                count={parcelNumbers.length}
                options={{ count: parcelNumbers.length }}
                defaults="You have {{count}} parcel(s) with this order."
              />
            </P2GText>
            <P2GText variant="body1" component="p" sx={{ py: 2 }}>
              <Trans t={t} i18nKey="HISTORY_OVERVIEW_MULTIPARCEL_SUBTITLE">
                To view tracking on each parcel go to tracking history.
              </Trans>
            </P2GText>
          </P2GGrid>
        )}
      </>
    );
  }

  if (parcelNumbers.length > 1 && isPerParcelNotification && currentParcel) {
    const lastTrackingEventScanCurrentParcel = GetLastTrackingEventTimeStampSingleParcel(currentParcel);
    const hasIssues = hasDeliveryIssue(currentParcel);

    const isNotTrackingInTransitCurrentParcel = lastTrackingEventScanCurrentParcel
      ? !currentParcel.delivered &&
        !estimatedDeliveryHasPassed &&
        !currentParcel.isRTS &&
        !currentParcel.isHeldInCustoms &&
        lastTrackingEventScanCurrentParcel.getTime() < GetRelativeTimestampByDays(4)
      : false;

    const isAFailedDeliveryAttempt =
      currentParcel.addressIssue || currentParcel.collectionAttempted || currentParcel.isCancelled ? true : false;

    return (
      <>
        {!currentParcel.delivered &&
          !withWindow &&
          !(hasIssues || currentParcel.isDelayed || currentParcel.isLost || isNotTrackingInTransitCurrentParcel) &&
          tracking.started && (
            <P2GGrid item container classNames={classes.textSection}>
              <P2GText variant="h5" component="p">
                <Trans
                  t={t}
                  i18nKey="HISTORY_COURIER"
                  defaults="Your {{ courier }} parcel is on it's way!"
                  values={{ courier: tracking.order.service.courierName }}
                />
              </P2GText>
              <P2GText variant="body1" component="p">
                <Trans t={t} i18nKey="HISTORY_SERVICE_LEVEL_AGREEMENT">
                  Please allow for 24hrs for the tracking to update.
                </Trans>
              </P2GText>
            </P2GGrid>
          )}

        {!currentParcel.delivered && withWindow && (
          <>
            <P2GGrid item>
              <TrackingWindow event={withWindow} started={tracking.started} headingVariant="h5" />
            </P2GGrid>
          </>
        )}

        {currentParcel.delivered &&
          !currentParcel.isRTS &&
          withWindow?.type !== 'Collection Scheduled' &&
          !isAFailedDeliveryAttempt && (
            <P2GGrid item container classNames={classes.textSection}>
              <P2GText variant="h5" dataTestId="tracking-notification-heading">
                <Trans t={t} i18nKey="RESULT_DELIVERED_TITLE" defaults="Your parcel has been delivered" />
              </P2GText>
              <P2GText variant="body1" component="p">
                <Trans
                  t={t}
                  i18nKey="RESULT_DELIVERED_DESCRIPTION"
                  defaults="Your parcel has been successfully delivered. <0>If there has been an issue with your parcel please click here</0>"
                  components={[<P2GLink href={`${websiteUrl}/service-centre/select/choose`} />]}
                />
              </P2GText>
            </P2GGrid>
          )}

        {currentParcel.delivered && currentParcel.isRTS && withWindow?.type !== 'Collection Scheduled' && (
          <P2GGrid item container classNames={classes.textSection}>
            <TrackingIssueMessage
              orderReference={tracking.order.orderLineId}
              color="error"
              title={t('RESULT_RTS_DELIVERED_TITLE', {
                defaultValue: 'Your {{courier}} parcel has been delivered back to the sender',
                courier: tracking.order.service.courierName
              })}
              message={
                <Trans
                  t={t}
                  i18nKey="RESULT_RTS_DELIVERED_DESCRIPTION"
                  defaults="Unfortunately the parcel has been returned to the sender due to an issue with the delivery, we apologise for any inconvenience.  If the sender has not received the parcel back please <0>click here</0>"
                  components={[<P2GLink href={`${websiteUrl}/service-centre/select/choose`} />]}
                />
              }
              showChat={false}
              showContact={false}
              headingVariant="h2"
              showOptions={true}
              onValidateOrder={onOpenValidation}
            />
          </P2GGrid>
        )}

        {tracking.order.foundOrder &&
          !tracking.started &&
          !tracking.order.service.isDropOff &&
          withWindow?.type !== 'Collection Scheduled' &&
          isAFailedDeliveryAttempt && (
            <P2GGrid item container classNames={classes.textSection}>
              <TrackingCollection
                hasAddressIssue={currentParcel.addressIssue}
                hasAttemptedCollection={currentParcel.collectionAttempted}
                hasCancelled={currentParcel.isCancelled}
                isOwner={tracking.isOwner}
                orderReference={tracking.order.orderLineId}
                headingVariant="h5"
              />
            </P2GGrid>
          )}

        {tracking.order.foundOrder &&
          !tracking.started &&
          tracking.order.service.isDropOff &&
          withWindow?.type !== 'Collection Scheduled' && (
            <P2GGrid item container classNames={classes.textSection}>
              <P2GText variant="h5">
                <Trans t={t} i18nKey="RESULT_DROPOFF_TITLE" defaults="Waiting for confirmation from the courier" />
              </P2GText>
              <P2GText variant="body1" component="p">
                <Trans
                  t={t}
                  i18nKey="RESULT_DROPOFF_DESCRIPTION"
                  defaults="We are waiting for confirmation that the courier has your parcel.  If you already dropped off your parcel, please allow up to 24 hrs for the tracking to update."
                />
              </P2GText>
            </P2GGrid>
          )}

        {tracking.started &&
          tracking.order.foundOrder &&
          (hasIssues || currentParcel.isDelayed || currentParcel.isLost || isNotTrackingInTransitCurrentParcel) &&
          withWindow?.type !== 'Collection Scheduled' &&
          !currentParcel.delivered && (
            <P2GGrid item container classNames={classes.textSection}>
              <TrackingIssues
                tracking={tracking}
                onOpenValidation={onOpenValidation}
                headingVariant="h5"
                courierName={tracking.order.service.courierName}
              />
            </P2GGrid>
          )}
      </>
    );
  }

  return null;
};

export default TrackingNotifications;
